// Branded PNG export for the dashboard "Today at a glance" card.
//
// Renders the FULL day's detail (not just the summary highlights) onto one
// or more offscreen canvases using the "paper" brand language (warm-white
// canvas, dark logo header, brass section headers, Cormorant title, IBM Plex
// Mono figures, hairline dividers, running footer) and saves them as high-DPI
// PNGs. Every category that has activity gets its own detailed section with
// the complete list of items.
//
// Long days are automatically SPLIT across multiple images (pages) so we
// never blow past the browser's max-canvas-size limit — which is what used to
// make the export silently fail (canvas.toBlob returned null).
//
// Usage: await downloadDailySummaryImage(dailySummaryData, { todayLabel });

import { format } from "date-fns";

// Paper-mode brand tokens (mirror pdfReport.js).
const PAPER = "#f7f4ed";
const INK = "#171511";
const INK_SOFT = "#4a463d";
const MUTED = "#6f6a5e";
const FAINT = "#8a8272";
const BRASS = "#a8823c";
const HAIRLINE = "#e6e0d2";

const SERIF = "'Cormorant Garamond', Georgia, serif";
const MONO = "'IBM Plex Mono', ui-monospace, monospace";
const SANS = "'Instrument Sans', Helvetica, Arial, sans-serif";

// Layout constants (CSS px, before the S device-scale).
const S = 2;
const W = 1080;
const PAD = 64;
const ROW_H = 32;
const SECTION_HEAD = 46;
const SECTION_GAP = 16;
const FOOTER_H = 72;          // reserved space below the content for the footer
// Max content height per page. Keeps each rendered canvas well under the
// browser's ~16k px hard limit (page height * S stays ~3.7k px).
const PAGE_CONTENT_MAX = 1500;

let _fontsReady = false;
async function _ensureFonts() {
  if (_fontsReady) return;
  try {
    const defs = [
      new FontFace("Cormorant Garamond", "url(/fonts/CormorantGaramond-600.ttf)", { weight: "600" }),
      new FontFace("IBM Plex Mono", "url(/fonts/IBMPlexMono-Regular.ttf)", { weight: "400" }),
      new FontFace("IBM Plex Mono", "url(/fonts/IBMPlexMono-Medium.ttf)", { weight: "500" }),
    ];
    (await Promise.all(defs.map((f) => f.load().catch(() => null)))).forEach((f) => f && document.fonts.add(f));
    await document.fonts.ready;
  } catch (_e) { /* system fonts */ }
  _fontsReady = true;
}

function _loadLogo() {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = "/brand/logo-dark.png";
  });
}

function _truncate(ctx, text, maxWidth) {
  text = String(text ?? "");
  if (ctx.measureText(text).width <= maxWidth) return text;
  let t = text;
  while (t.length > 1 && ctx.measureText(`${t}…`).width > maxWidth) t = t.slice(0, -1);
  return `${t}…`;
}

function _letterspace(ctx, text, x, y, spacing, align = "left") {
  if (align === "right") {
    let total = 0;
    for (const ch of text) total += ctx.measureText(ch).width + spacing;
    total -= spacing;
    x -= total;
  }
  let cx = x;
  for (const ch of text) { ctx.fillText(ch, cx, y); cx += ctx.measureText(ch).width + spacing; }
}

function _hline(ctx, x1, x2, y) {
  ctx.beginPath(); ctx.moveTo(x1, y); ctx.lineTo(x2, y); ctx.stroke();
}

function _safeDate(iso, fmt = "EEEE, d MMMM yyyy") {
  try {
    const d = new Date(iso && iso.length <= 10 ? `${iso}T00:00:00` : iso);
    return Number.isNaN(d.getTime()) ? String(iso) : format(d, fmt);
  } catch (_e) { return String(iso); }
}

// ── Build the ordered list of detail sections from the payload ────────
function _buildSections(data) {
  const H = data?.horses || {};
  const sections = [];
  const push = (title, rows) => { if (rows && rows.length) sections.push({ title, rows }); };

  // New horses
  const added = H.added || {};
  if (added.count_today > 0) {
    const names = (added.names || []).length ? added.names : [`${added.count_today} added`];
    push(`New horses · ${added.count_today}`, names.map((n) => ({ label: n })));
  }

  // Ridden today
  const ridden = (data?.chukkas?.ridden_today) || [];
  if (ridden.length) {
    push(`Ridden today · ${ridden.length}`, ridden.map((c) => ({
      label: (c.horse || "Unknown") + (c.rider ? ` — ${c.rider}` : ""),
      value: c.score != null ? `${Number(c.score).toFixed(1)}` : "",
    })));
  }

  // Yard moves
  const moves = H.location_changes || [];
  if (moves.length) {
    push(`Yard moves · ${moves.length}`, moves.map((m) => ({
      label: m.horse, value: `${m.from || "—"} \u2192 ${m.to || "—"}`,
    })));
  }

  // Work-status changes
  const ws = H.work_status_changes || [];
  if (ws.length) {
    push(`Work-status changes · ${ws.length}`, ws.map((m) => ({
      label: m.horse, value: `${m.from || "—"} \u2192 ${m.to || "—"}`,
    })));
  }

  // Health updates
  const inj = H.injury_changes || [];
  if (inj.length) {
    push(`Health updates · ${inj.length}`, inj.map((m) => {
      let v = "";
      if (m.injured_to === true) v = `injured${m.severity_to ? ` · ${m.severity_to}` : ""}`;
      else if (m.injured_to === false) v = "recovered";
      else if (m.severity_to) v = m.severity_to;
      return { label: m.horse, value: v };
    }));
  }

  // Feed used
  const feed = (data?.feed_trend || []).filter((f) => (f.used_today || 0) > 0 || (f.used_yesterday || 0) > 0);
  if (feed.length) {
    push("Feed used", feed.map((f) => {
      let pct = "";
      if (f.delta_pct != null && f.delta) pct = ` (${f.delta > 0 ? "+" : "-"}${Math.abs(f.delta_pct).toFixed(0)}%)`;
      return { label: (f.item || "").replace(/\b\w/g, (c) => c.toUpperCase()),
               value: `${f.used_today ?? 0}${f.unit ? " " + f.unit : ""}${pct}` };
    }));
  }

  // Spend today (invoices uploaded)
  const inv = data?.invoices || {};
  if (inv.count_today > 0) {
    const totals = inv.totals_today || {};
    const rows = Object.entries(totals).map(([cur, amt]) => ({
      label: cur, value: `${Number(amt).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    }));
    if (!rows.length) rows.push({ label: "Amount", value: "—" });
    push(`Spend today · ${inv.count_today} invoice${inv.count_today !== 1 ? "s" : ""}`, rows);
  }

  // Other updates
  const other = H.other_changes || [];
  if (other.length) {
    push(`Other horse updates · ${other.length}`, other.map((o) => ({
      label: o.horse, value: _changeList(o.changes).slice(0, 3).join(", "),
    })));
  }

  return sections;
}

// Normalise a horse "changes" value into a string[] — the API sometimes
// sends an array, sometimes a single string, and sometimes a { field: value }
// object, so we can't assume `.slice` exists on it.
function _changeList(changes) {
  if (Array.isArray(changes)) return changes.map((c) => String(c));
  if (changes == null) return [];
  if (typeof changes === "string") return changes.trim() ? [changes] : [];
  if (typeof changes === "object") {
    return Object.entries(changes).map(([k, v]) =>
      v && typeof v === "object" && ("from" in v || "to" in v)
        ? `${k}: ${v.from ?? "—"} \u2192 ${v.to ?? "—"}`
        : `${k}: ${v}`
    );
  }
  return [String(changes)];
}

// ── Split sections into bounded pages ─────────────────────────────────
// Each page is a list of blocks: { title, rows, cont }. A section too tall
// for one page is broken across pages, and the continued portion is marked
// with `cont: true` so we can annotate its header with "(cont.)".
function _paginate(sections) {
  if (!sections.length) return [[]];
  const pages = [];
  let cur = [];
  let curH = 0;
  const flush = () => { if (cur.length) { pages.push(cur); cur = []; curH = 0; } };

  for (const s of sections) {
    let remaining = s.rows;
    let firstChunk = true;
    while (remaining.length) {
      const avail = PAGE_CONTENT_MAX - curH;
      // Need room for at least a header + one row; otherwise start a new page.
      if (avail < SECTION_HEAD + ROW_H + SECTION_GAP && cur.length) { flush(); continue; }
      const usable = (cur.length ? PAGE_CONTENT_MAX - curH : PAGE_CONTENT_MAX) - SECTION_HEAD - SECTION_GAP;
      const fit = Math.max(1, Math.floor(usable / ROW_H));
      const take = remaining.slice(0, fit);
      cur.push({ title: s.title, rows: take, cont: !firstChunk });
      curH += SECTION_HEAD + take.length * ROW_H + SECTION_GAP;
      remaining = remaining.slice(fit);
      firstChunk = false;
      if (remaining.length) flush();
    }
  }
  flush();
  return pages.length ? pages : [[]];
}

// ── Render a single page canvas ───────────────────────────────────────
function _renderPage({ blocks, logo, dateLabel, pageNum, pageCount, pageHeightOverride }) {
  // Measure height for this page's blocks.
  const contentStartOffset = 40; // gap after the date line to first section
  let contentH = 0;
  if (!blocks.length) {
    contentH = 40;
  } else {
    for (const b of blocks) contentH += SECTION_HEAD + b.rows.length * ROW_H + SECTION_GAP;
  }

  const canvas = document.createElement("canvas");
  // We defer the exact height until after the title block is measured; the
  // title block height is fixed, so compute it up front.
  const TITLE_BLOCK_H = 52 /*rule offset*/ + 46 /*brief*/ + 32 /*title*/ + 44 /*date*/;
  const headerTopH = 46 + 40 + 18; // logo band + rule padding
  const naturalH = Math.max(560, headerTopH + TITLE_BLOCK_H + contentStartOffset + contentH + FOOTER_H);
  // A uniform override keeps every page the same size (used for clean PDFs).
  const pageH = pageHeightOverride ? Math.max(naturalH, pageHeightOverride) : naturalH;

  canvas.width = W * S; canvas.height = pageH * S;
  const ctx = canvas.getContext("2d");
  ctx.scale(S, S);
  ctx.fillStyle = PAPER; ctx.fillRect(0, 0, W, pageH);

  // Header
  const hy = 46, logoSize = 40;
  if (logo) { try { ctx.drawImage(logo, PAD, hy, logoSize, logoSize); } catch (_e) { /* noop */ } }
  ctx.fillStyle = INK; ctx.font = `500 15px ${SANS}`; ctx.textBaseline = "middle";
  _letterspace(ctx, "AFRICAN POLISTAS OPERATIONS", PAD + logoSize + 16, hy + logoSize / 2, 2);
  ctx.fillStyle = BRASS; ctx.font = `600 13px ${SANS}`;
  _letterspace(ctx, "CONFIDENTIAL", W - PAD, hy + logoSize / 2, 2, "right");
  ctx.textBaseline = "alphabetic";
  const ruleY = hy + logoSize + 18;
  ctx.strokeStyle = HAIRLINE; ctx.lineWidth = 1; _hline(ctx, PAD, W - PAD, ruleY);

  // Title block
  let cy = ruleY + 52;
  ctx.fillStyle = BRASS; ctx.font = `600 14px ${SANS}`; _letterspace(ctx, "DAILY BRIEF", PAD, cy, 3);
  cy += 46;
  ctx.fillStyle = INK; ctx.font = `600 52px ${SERIF}`; ctx.fillText("Today at a glance", PAD, cy);
  cy += 32;
  ctx.fillStyle = MUTED; ctx.font = `400 18px ${SANS}`;
  const dateSuffix = pageCount > 1 ? `  \u00b7  SAST  \u00b7  continued (${pageNum}/${pageCount})`
                                   : `  \u00b7  SAST`;
  ctx.fillText(`${dateLabel}${dateSuffix}`, PAD, cy);
  cy += 44;

  // Sections
  const contentW = W - PAD * 2;
  if (!blocks.length) {
    ctx.fillStyle = MUTED; ctx.font = `400 20px ${SANS}`;
    ctx.fillText("Quiet day so far. No new activity since midnight.", PAD, cy + 6);
  } else {
    for (const b of blocks) {
      // Section header (annotate continued chunks)
      ctx.fillStyle = BRASS; ctx.font = `600 13px ${SANS}`;
      const title = b.cont ? `${b.title} (CONT.)` : b.title;
      _letterspace(ctx, title.toUpperCase(), PAD, cy, 2);
      ctx.strokeStyle = INK; ctx.lineWidth = 1.4; _hline(ctx, PAD, W - PAD, cy + 10);
      cy += SECTION_HEAD;
      // Rows
      for (const row of b.rows) {
        ctx.fillStyle = INK_SOFT; ctx.font = `400 18px ${SANS}`;
        const hasVal = row.value != null && row.value !== "";
        const labelMax = hasVal ? contentW - 260 : contentW;
        ctx.fillText(_truncate(ctx, row.label, labelMax), PAD, cy + 20);
        if (hasVal) {
          ctx.fillStyle = INK; ctx.font = `500 17px ${MONO}`; ctx.textAlign = "right";
          ctx.fillText(_truncate(ctx, row.value, 250), W - PAD, cy + 20);
          ctx.textAlign = "left";
        }
        ctx.strokeStyle = HAIRLINE; ctx.lineWidth = 0.5; _hline(ctx, PAD, W - PAD, cy + 30);
        cy += ROW_H;
      }
      cy += SECTION_GAP;
    }
  }

  // Footer
  const footerY = pageH - 40;
  ctx.strokeStyle = HAIRLINE; ctx.lineWidth = 1; _hline(ctx, PAD, W - PAD, footerY - 16);
  ctx.fillStyle = MUTED; ctx.font = `400 14px ${SANS}`;
  const footL = pageCount > 1
    ? `Operations Report  \u00b7  The Crags, Plettenberg Bay  \u00b7  Page ${pageNum} of ${pageCount}`
    : "Operations Report  \u00b7  The Crags, Plettenberg Bay";
  ctx.fillText(footL, PAD, footerY + 4);
  ctx.fillStyle = FAINT; ctx.font = `400 14px ${MONO}`; ctx.textAlign = "right";
  ctx.fillText(`Generated ${format(new Date(), "d MMM yyyy, HH:mm")}`, W - PAD, footerY + 4);
  ctx.textAlign = "left";

  return canvas;
}

// ── Public: render all pages (one canvas per page) ────────────────────
// `uniform: true` makes every page the same (max) height — ideal for a
// clean multi-page PDF where all pages share one format.
export async function renderDailySummaryPages(data, { todayLabel, uniform = false } = {}) {
  await _ensureFonts();
  const logo = await _loadLogo();
  const sections = _buildSections(data);
  const dateLabel = todayLabel || (data?.today_sast ? _safeDate(data.today_sast) : format(new Date(), "EEEE, d MMMM yyyy"));
  const pages = _paginate(sections);
  // Uniform page height = full chrome + a full content page + footer.
  const TITLE_BLOCK_H = 52 + 46 + 32 + 44;
  const headerTopH = 46 + 40 + 18;
  const uniformH = uniform ? (headerTopH + TITLE_BLOCK_H + 40 + PAGE_CONTENT_MAX + FOOTER_H) : 0;
  return pages.map((blocks, i) =>
    _renderPage({ blocks, logo, dateLabel, pageNum: i + 1, pageCount: pages.length, pageHeightOverride: uniformH })
  );
}

// Backward-compatible single-canvas renderer (first page only).
export async function renderDailySummaryCanvas(data, opts = {}) {
  const pages = await renderDailySummaryPages(data, opts);
  return pages[0];
}

function _canvasToBlob(canvas) {
  return new Promise((resolve) => {
    try {
      canvas.toBlob((b) => {
        if (b) return resolve(b);
        // Fallback for environments where toBlob yields null.
        try {
          const dataUrl = canvas.toDataURL("image/png");
          const bin = atob(dataUrl.split(",")[1]);
          const arr = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
          resolve(new Blob([arr], { type: "image/png" }));
        } catch (_e) { resolve(null); }
      }, "image/png");
    } catch (_e) { resolve(null); }
  });
}

function _download(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function downloadDailySummaryImage(data, opts = {}) {
  const canvases = await renderDailySummaryPages(data, opts);
  const stamp = format(new Date(), "yyyyMMdd-HHmm");
  const total = canvases.length;

  let saved = 0;
  for (let i = 0; i < total; i++) {
    const blob = await _canvasToBlob(canvases[i]);
    if (!blob) continue;
    const name = total > 1
      ? `today-at-a-glance-${stamp}-${i + 1}of${total}.png`
      : `today-at-a-glance-${stamp}.png`;
    _download(blob, name);
    saved += 1;
    // Small stagger so the browser reliably fires each download.
    if (i < total - 1) await new Promise((r) => setTimeout(r, 400));
  }

  if (!saved) throw new Error("Image generation failed (empty canvas output)");
  return { pages: total };
}

// ── PDF export: full day as a clean multi-page PDF ────────────────────
export async function downloadDailySummaryPDF(data, opts = {}) {
  const canvases = await renderDailySummaryPages(data, { ...opts, uniform: true });
  if (!canvases.length) throw new Error("Nothing to export");
  const { jsPDF } = await import("jspdf");

  // Page format in points = the canvas CSS px size (W × pageH). All pages
  // are uniform, so we size the doc from the first page.
  const pageWpt = W;
  const pageHpt = canvases[0].height / S;

  const doc = new jsPDF({ unit: "px", format: [pageWpt, pageHpt], orientation: "portrait", compress: true });
  canvases.forEach((c, i) => {
    if (i > 0) doc.addPage([pageWpt, pageHpt], "portrait");
    const img = c.toDataURL("image/png");
    doc.addImage(img, "PNG", 0, 0, pageWpt, pageHpt, undefined, "FAST");
  });

  const stamp = format(new Date(), "yyyyMMdd-HHmm");
  doc.save(`today-at-a-glance-${stamp}.pdf`);
  return { pages: canvases.length };
}

// ── WhatsApp share: native share sheet (pick a chat) → image files ────
// Uses the Web Share API with files where available (mobile + some
// desktops) so the exec can drop the image straight into any WhatsApp
// chat. Falls back to downloading the image(s) and opening WhatsApp with
// a prefilled caption when file-sharing isn't supported.
export async function shareDailySummaryImage(data, opts = {}) {
  const canvases = await renderDailySummaryPages(data, opts);
  const stamp = format(new Date(), "yyyyMMdd-HHmm");
  const label = opts?.todayLabel || "today";

  // Build File objects from each page.
  const files = [];
  for (let i = 0; i < canvases.length; i++) {
    const blob = await _canvasToBlob(canvases[i]);
    if (!blob) continue;
    const name = canvases.length > 1
      ? `today-at-a-glance-${stamp}-${i + 1}of${canvases.length}.png`
      : `today-at-a-glance-${stamp}.png`;
    files.push(new File([blob], name, { type: "image/png" }));
  }
  if (!files.length) throw new Error("Image generation failed (empty canvas output)");

  const caption = `African Polistas — Today at a glance (${label})`;

  // Preferred path: native share sheet with the image attached.
  try {
    if (navigator.canShare && navigator.canShare({ files }) && navigator.share) {
      await navigator.share({ files, title: "Today at a glance", text: caption });
      return { method: "share", pages: files.length };
    }
  } catch (e) {
    // User cancelled the share sheet — treat as a no-op, not an error.
    if (e && (e.name === "AbortError" || e.name === "NotAllowedError")) {
      return { method: "cancelled", pages: files.length };
    }
    // Otherwise fall through to the download + wa.me fallback.
  }

  // Fallback: save the image(s) locally, then open WhatsApp with a caption
  // the user can paste alongside the just-saved image.
  for (let i = 0; i < files.length; i++) {
    _download(files[i], files[i].name);
    if (i < files.length - 1) await new Promise((r) => setTimeout(r, 400));
  }
  const waUrl = `https://wa.me/?text=${encodeURIComponent(caption + " — image saved to your device, attach it here.")}`;
  window.open(waUrl, "_blank", "noopener,noreferrer");
  return { method: "fallback", pages: files.length };
}

